// Business Profile → Quote Branding. Everything a contractor's customer sees
// on the digital proposal: cover hero, accent colour, tagline and the default
// pricing-display / visibility rules (each quote can override them).
import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Image as ImageIcon, Upload, Trash2, Save, Palette } from "lucide-react";
import { toast } from "sonner";

export const ACCENT_PRESETS = [
  ["orange", "#FF5F15"], ["blue", "#0284C7"], ["green", "#059669"],
  ["purple", "#7C3AED"], ["grey", "#52525B"],
];

export const PRICING_DISPLAYS = [
  ["detailed", "Detailed — description, qty, unit, rate & total"],
  ["standard", "Standard — description & total only"],
  ["section_summary", "Section summary — one price per section"],
  ["lump_sum", "Lump sum — project total only"],
];

export const VISIBILITY_ROWS = [
  ["show_item_images", "Show item images"],
  ["group_charge_up_items", "Group charge-up invoice items"],
  ["show_job_reference", "Show job on invoices"],
  ["show_descriptions", "Show item descriptions"],
  ["show_quantities", "Show quantities"],
  ["show_unit_prices", "Show unit prices"],
  ["show_section_subtotals", "Show section subtotals"],
  ["show_discount", "Show discount"],
  ["show_business_address", "Show business address"],
  ["show_contact_details", "Show contact details"],
];

const DEFAULTS = {
  accent_color: "orange", tagline: "", business_name: "", pricing_display: "detailed",
  business_hours: "", group_charge_up_items: true, show_job_reference: true,
  linkedin_url: "", facebook_url: "", instagram_url: "", cover_disabled: false,
  show_item_images: true, show_descriptions: true, show_quantities: true, show_unit_prices: true,
  show_section_subtotals: true, show_discount: true, show_business_address: true, show_contact_details: true,
};

export default function QuoteBrandingSettings({ canManage, info, reload }) {
  const [form, setForm] = useState(DEFAULTS);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [bust, setBust] = useState(0);
  const fileRef = useRef(null);
  const coverPath = info?.quote_branding?.cover_path;

  useEffect(() => {
    setForm({ ...DEFAULTS, ...(info?.quote_branding || {}), accent_color: info?.quote_branding?.accent_color || null });
  }, [info]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      await api.patch("/org/me", { quote_branding: { ...(info?.quote_branding || {}), ...form } });
      toast.success("Document branding saved — quotes and invoices use these defaults");
      await reload?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast.error("Cover image too large (max 8MB)"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/org/branding/quote-cover", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Quote cover image updated");
      setBust(Date.now());
      await reload?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const removeCover = async () => {
    if (!window.confirm("Remove the quote cover image? Quotes fall back to the Fieldlink default hero.")) return;
    try {
      await api.delete("/org/branding/quote-cover");
      toast.success("Cover image removed");
      await reload?.();
    } catch (e) { toast.error("Remove failed"); }
  };

  const tok = typeof window !== "undefined" ? localStorage.getItem("fl_token") || "" : "";
  const coverUrl = coverPath
    ? `${api.defaults.baseURL}/files/view?path=${encodeURIComponent(coverPath)}&auth=${encodeURIComponent(tok)}&v=${bust}`
    : `${api.defaults.baseURL}/public/quote-hero-default.jpg`;
  const inherited = info?.document_branding_fallback;
  const accentValue = form.accent_color || inherited?.primary_colour || "orange";
  const accentHex = accentValue.startsWith("#") ? accentValue
    : (ACCENT_PRESETS.find(([k]) => k === accentValue) || ACCENT_PRESETS[0])[1];

  return (
    <div data-testid="quote-branding-settings">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-5">
        <div>
          <div className="label-tag flex items-center gap-1.5"><Palette className="w-3 h-3" /> Document branding</div>
          <h2 className="font-display font-extrabold uppercase tracking-tight text-xl mt-2">
            How your quotes &amp; invoices look to customers
          </h2>
          <p className="text-xs text-zinc-500 mt-1 max-w-xl">
            Shared branding for every customer-facing quote and invoice — the digital document, PDF and email. Any quote or invoice can override these in its own settings.
          </p>
        </div>
        {canManage && (
          <Button onClick={save} disabled={busy}
            className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white uppercase tracking-wider font-bold text-xs"
            data-testid="quote-branding-save">
            <Save className="w-3.5 h-3.5 mr-1.5" /> {busy ? "Saving…" : "Save document branding"}
          </Button>
        )}
      </div>

      {inherited?.available && <p className="mb-5 rounded-lg bg-muted/40 p-4 text-sm">
        White Label workspace branding supplies defaults where no document override is set.
        {inherited.workspace_logo && " Logo: Company branding."}
        {!form.accent_color && inherited.primary_colour && " Primary colour: Company branding."}
        {" "}Set a business name or colour below to override it. Clear the name or use the inherited colour to restore the fallback. Issued documents keep their original branding.
      </p>}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* cover */}
        <div>
          <Label className="label-tag">Document cover image (hero)</Label>
          <div className="mt-2 relative border-2 border-zinc-200 overflow-hidden bg-zinc-100 h-[170px]" data-testid="quote-cover-preview">
            <img src={coverUrl} alt="Quote cover preview" className="w-full h-full object-cover" />
            <div className="absolute inset-0" style={{ background: "linear-gradient(100deg,rgba(9,9,11,0.9),rgba(9,9,11,0.55))" }} />
            <div className="absolute inset-0 p-4 flex flex-col justify-between">
              <div>
                <div className="text-white font-display font-black uppercase tracking-tight text-lg leading-none">
                  {form.business_name || inherited?.display_name || info?.name || "Your business"}
                </div>
                {form.tagline && <div className="text-[9px] tracking-[0.28em] uppercase text-zinc-300 mt-1.5">{form.tagline}</div>}
              </div>
              <span className="self-start px-2 py-0.5 text-[9px] font-black uppercase tracking-wider text-white" style={{ background: accentHex }}>
                Quote Q0001
              </span>
            </div>
          </div>
          {canManage && (
            <div className="mt-3 flex flex-wrap gap-2">
              <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/jpg,image/webp" className="hidden"
                onChange={onUpload} data-testid="quote-cover-input" />
              <Button onClick={() => fileRef.current?.click()} disabled={uploading} variant="outline"
                className="rounded-sm border-zinc-400 uppercase tracking-wider font-bold text-xs" data-testid="quote-cover-upload">
                <Upload className="w-3.5 h-3.5 mr-1.5" /> {uploading ? "Uploading…" : coverPath ? "Replace cover" : "Upload cover"}
              </Button>
              {coverPath && (
                <Button onClick={removeCover} variant="outline"
                  className="rounded-sm border-zinc-400 text-zinc-700 uppercase tracking-wider font-bold text-xs" data-testid="quote-cover-remove">
                  <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Remove
                </Button>
              )}
              <label className="flex items-center gap-2 text-xs text-zinc-600 ml-1">
                <Switch checked={!form.cover_disabled} onCheckedChange={(v) => set("cover_disabled", !v)}
                  className="data-[state=checked]:bg-[#FF5F15] scale-90" data-testid="quote-cover-enabled" />
                Use a hero image
              </label>
            </div>
          )}
          {!coverPath && (
            <p className="text-[11px] text-zinc-400 mt-2 flex items-start gap-1.5">
              <ImageIcon className="w-3.5 h-3.5 mt-px shrink-0" />
              Using the Fieldlink default civil hero. Upload your own site photo to make it yours (JPG/PNG, landscape, max 8MB).
            </p>
          )}
        </div>

        {/* identity + accent */}
        <div className="space-y-4">
          <div>
            <Label className="label-tag" htmlFor="qb-name">Business name on quotes</Label>
            <Input id="qb-name" value={form.business_name} onChange={(e) => set("business_name", e.target.value)} disabled={!canManage}
              placeholder={inherited?.display_name || info?.name || "Defaults to your trading name"}
              className="mt-1.5 rounded-sm border-zinc-400" data-testid="quote-brand-name" />
          </div>
          <div>
            <Label className="label-tag" htmlFor="qb-tagline">Quote tagline</Label>
            <Input id="qb-tagline" value={form.tagline} onChange={(e) => set("tagline", e.target.value)} disabled={!canManage}
              placeholder="e.g. Earthworks • Drainage • Siteworks"
              className="mt-1.5 rounded-sm border-zinc-400" data-testid="quote-brand-tagline" />
          </div>
          <div>
            <Label className="label-tag">Quote accent colour</Label>
            {inherited?.available && <label className="flex items-center gap-2 text-sm mt-2"><input type="checkbox" disabled={!canManage} checked={!form.accent_color}
              onChange={event => set("accent_color", event.target.checked ? null : accentHex)} />Use inherited company colour</label>}
            <div className="flex items-center gap-2.5 mt-2">
              {ACCENT_PRESETS.map(([key, hex]) => (
                <button key={key} type="button" onClick={() => canManage && set("accent_color", key)} title={key}
                  className={`w-7 h-7 rounded-full ${form.accent_color === key ? "ring-2 ring-offset-2 ring-zinc-900" : "opacity-70 hover:opacity-100"}`}
                  style={{ background: hex }} data-testid={`quote-accent-${key}`} />
              ))}
              <input type="color" value={accentHex} disabled={!canManage}
                onChange={(e) => set("accent_color", e.target.value.toUpperCase())}
                className="w-9 h-9 rounded-sm border border-zinc-300 bg-white cursor-pointer"
                title="Custom colour" data-testid="quote-accent-custom" />
              <span className="text-[11px] font-mono text-zinc-500">{accentHex}</span>
            </div>
          </div>
          <div>
            <Label className="label-tag" htmlFor="qb-hours">Business hours (shown in &ldquo;Need help?&rdquo; on invoices)</Label>
            <Input id="qb-hours" value={form.business_hours} onChange={(e) => set("business_hours", e.target.value)} disabled={!canManage}
              placeholder="e.g. Mon – Fri, 7:00am – 5:00pm"
              className="mt-1.5 rounded-sm border-zinc-400" data-testid="quote-brand-hours" />
          </div>
          <div>
            <Label className="label-tag">Default customer pricing display</Label>
            <Select value={form.pricing_display} onValueChange={(v) => set("pricing_display", v)} disabled={!canManage}>
              <SelectTrigger className="mt-1.5 rounded-sm border-zinc-400 text-sm" data-testid="quote-pricing-display">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PRICING_DISPLAYS.map(([k, label]) => (
                  <SelectItem key={k} value={k} data-testid={`pricing-display-${k}`}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* visibility */}
        <div className="lg:col-span-2 border-t border-zinc-200 pt-5">
          <Label className="label-tag">What customers see</Label>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-3 mt-3">
            {VISIBILITY_ROWS.map(([k, label]) => (
              <label key={k} className="flex items-center justify-between gap-3 cursor-pointer select-none">
                <span className="text-xs text-zinc-700">{label}</span>
                <Switch checked={form[k] !== false} onCheckedChange={(v) => set(k, v)} disabled={!canManage}
                  className="data-[state=checked]:bg-[#FF5F15] scale-90" data-testid={`quote-brand-${k}`} />
              </label>
            ))}
          </div>
        </div>

        {/* socials */}
        <div className="lg:col-span-2 border-t border-zinc-200 pt-5 grid sm:grid-cols-3 gap-4">
          {[["linkedin_url", "LinkedIn"], ["facebook_url", "Facebook"], ["instagram_url", "Instagram"]].map(([k, label]) => (
            <div key={k}>
              <Label className="label-tag" htmlFor={`qb-${k}`}>{label}</Label>
              <Input id={`qb-${k}`} value={form[k] || ""} onChange={(e) => set(k, e.target.value)} disabled={!canManage}
                placeholder="https://…" className="mt-1.5 rounded-sm border-zinc-400 text-sm" data-testid={`quote-social-${k}`} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
